/**
 * Wire types mirrored from the frozen machine-to-machine contract
 * `openapi/openapi.yaml` (WiCore ACS Integration API v1) and
 * `schemas/cloudevents/*.json`.
 *
 * Rules for this file (it is the console's only link to the contract):
 *  - Field names are the wire names (`snake_case`), never camelCased — the
 *    console talks to the API exactly as documented, no server-side reshaping.
 *  - Nullability follows the schema (`nullable: true` → `T | null`).
 *  - Optional-ness follows `required:` in the schema.
 *  - No `any` — open payloads use `unknown`.
 *
 * Do not "improve" a shape here: if the contract changes, the contract file
 * changes first and this one follows in the same commit.
 */

/* ------------------------------------------------------------------ */
/* RFC 9457 problem+json — `components/schemas/Problem`               */
/* ------------------------------------------------------------------ */

/**
 * The stable error codes, verbatim from the OpenAPI `Problem.code` enum
 * (`acs_service/problems.py` is the server-side catalogue). Covered by a drift
 * test that parses the OpenAPI file — see `src/api/contract.test.ts`.
 */
export const PROBLEM_CODES = [
  'acs-unreachable',
  'device-not-found',
  'device-offline',
  'binding-conflict',
  'action-unsupported',
  'policy-conflict',
  'job-timeout',
  'job-not-found',
  'contract-version-unsupported',
  'stale-snapshot',
  'reference-invalid',
  'scope-insufficient',
  'rate-limited',
  'idempotency-conflict',
  // U-03-a (plan ruling D3): the guard's second 409, and a different fact from the line above —
  // here the request is *identical* and its effect is still running. Additive, mirroring the
  // published enum; `parseProblem` rejects a code this list does not carry.
  'idempotency-in-progress',
  'signature-invalid',
  'olt-not-found',
  'adapter-unreachable',
  // U-05/C1d (AUD-05/G-OLT-13): the two honest answers the façade owes a caller whose OLT
  // link is not usable. `adapter-unconfigured` is NOT `adapter-unreachable`: the adapter
  // answered and refused — it is up, and this deployment has no configured link to it — so
  // the next step is a configuration, not a service. `adapter-state-unavailable` is a third
  // fact again: the adapter took the write and could not persist it, so the operation did
  // not happen and a retry is safe. Additive: no existing code changes its meaning.
  'adapter-unconfigured',
  'adapter-state-unavailable',
  // C9 (the OLT operations): the adapter ANSWERED and what it said does not match the
  // request it was given — a bulk run that reports a different number of outcomes than it
  // had targets. It is deliberately NOT `adapter-unreachable` («we could not reach it»):
  // the adapter is up, it replied, and the next step is a version of that service, not a
  // service that is down. 502, like the other upstream facts.
  'adapter-answer-invalid',
  // G-PRV-05 (2026-10-30): the preset write door's upstream refusal. Deliberately
  // NOT `acs-unreachable` — the NBI ANSWERED and rejected the document («it will not
  // take this», not «we could not reach it»); the same upstream-side lesson the OLT
  // side learned in DA-47/50. 502, like the other upstream facts.
  'acs-refused',
  'firmware-not-found',
  'campaign-not-found',
  'alarm-not-found',
  // `report-not-found` is the reconciliation-run 404 of coverage §2, declared in
  // the same `Problem.code` enum (`acs_service/problems.py`). The page that
  // consumes it is plan page 10 (W2b), not W2a — it is mirrored here because the
  // drift guard compares this list with the document, and a consumer must never
  // reject a *known* code as an unknown one.
  'report-not-found',
  // The circuits that came after T9. Each is a *different fact* from the code it resembles, which is
  // why the server gave it its own word: `capability-unproven` is not `scope-insufficient` («we lack
  // evidence», not «you lack permission»), `not-an-action` is not `operation-not-found` (the slot
  // exists, it is a parameter), and `job-not-cancellable` is not `job-not-found` (the job concluded).
  'capability-unproven',
  'operation-not-found',
  'confirmation-required',
  'manifest-not-found',
  'not-an-action',
  'job-not-cancellable',
  // E2 (G-FW-02) gave the registry a refusal of its own. It is *not* `reference-invalid`: the
  // declaration was well formed, the bytes simply are not the file the operator named — which is a
  // fact about the image, and the remedy (re-download / re-hash the source file) is not «fix the body».
  'checksum-mismatch',
  // H4-b's dead-letter doors raised these two for a whole circuit without the contract naming
  // them — the defect class the drift guard in `tests/test_acs_h8b_backup_doors.py` now closes.
  // `event-not-found` is not `alarm-not-found`: one misses a delivery record, the other an alarm.
  'event-not-found',
  'event-not-redrivable',
  // Circuit H's admin doors. These four were raisable by the façade before this list carried them,
  // which is the failure mode the drift guard exists for: `parseProblem` rejects an unmirrored code
  // and the operator gets «unknown problem code» instead of «run --baseline first».
  // `migration-unrecorded` is not `contract-version-unsupported` (the build is fine, the *record* is
  // missing) and `backup-dir-unavailable` is not `backup-not-found` (a missing file is a filename
  // problem; a missing directory is a mount problem) — opposite actions, opposite words.
  'migration-unrecorded',
  'migration-ahead',
  // DA-38 — the two single-flight refusals. Both are 409 conflicts with a live peer
  // (another migrator), not build errors: the door is right and must simply be re-run.
  'migration-busy',
  'migration-lost-race',
  'backup-not-found',
  'backup-dir-unavailable',
  'legal-hold-not-found',
  'legal-hold-already-lifted',
  // R1's identity doors (`docs/RBAC_AND_CONSOLE_FA.md` §۶). `auth-unconfigured` is 503 and not 401 on
  // purpose: "you are not allowed" sends an operator looking for a credential, while "this deployment
  // has no active owner" sends them to the deploy — and the login screen has to say the second one.
  // `last-owner` is the §۳-۲ guard: the refusal that keeps a deployment from deleting its own last way
  // in. `user-exists` is the duplicate-username conflict, and `user-not-found` addresses a user row the
  // way `device-not-found` addresses a device.
  'auth-unconfigured',
  'user-not-found',
  'user-exists',
  'last-owner',
  // U-06 (`REQ-PROFILE`) — the operator profile's three refusals. Each is a different fact and the
  // console has to say which one: `profile-invalid` is «the document does not validate, and the
  // report below lists all of it» (never `reference-invalid`, whose sentence sends the operator to
  // the schema instead of to a fixable list), `profile-stale-version` is «another activation landed
  // first — re-read and diff», and `profile-release-blocked` is the one the operator has *permission*
  // for and the deployment's own claim refuses: every complaint in its report is one of the two
  // release rules (§۲:۴۳ mock in production, :۴۴ an incomplete secret reference), so nothing about
  // the document's shape or vocabulary is wrong. Note that the validation report still answers
  // `valid: false` there — `valid` counts every rule including those two, and `release_blocked` says
  // *which* complaints there are rather than overriding the verdict. `scope-insufficient` would send
  // them hunting for a key that was never the problem, and a 422 would send them to edit the document
  // when the thing that has to change is what this deployment declares about itself.
  'profile-invalid',
  'profile-stale-version',
  'profile-release-blocked',
  // U-06-d's fourth profile code, and the only *lookup* one: `:rollback` addresses a history row by
  // number, and a row that is not there (never minted, or trimmed by the bounded window) is a 404
  // about this deployment's ledger — not a verdict on a document (`profile-invalid` would blame bytes
  // the operator never submitted here) and not a lost race (`profile-stale-version` would say the
  // version exists and somebody else moved the active pointer first).
  'profile-version-not-found',
  // N1/N2 (G-ALM-03/06) — the alarm lifecycle's refusals. Each one is a different fact about a
  // row, and the console has to say which: `alarm-already-cleared` is «the closure stands and is
  // not repeated» (never `alarm-not-found` — the row is there, it says who closed it),
  // `alarm-not-escalatable` / `alarm-not-assignable` / `alarm-not-ackable` are the three doors a
  // CLOSED record refuses (a closed alarm moves no more), and they keep their own words because
  // their remedies differ. `actor-not-human` is the one refusal that is not about a permission at
  // all: these doors record a named person's responsibility, and a machine assertion is not a
  // person — `scope-insufficient` would send the operator looking for a key that was never the
  // problem.
  'alarm-already-cleared',
  'alarm-not-escalatable',
  'alarm-not-assignable',
  'alarm-not-ackable',
  'actor-not-human',
  // S1 (G-SUB-01/03) — the subscriber surface's four words. `subscriber-disabled` is 503 and is
  // NOT a lookup miss: it says «this deployment does not run the subscriber surface», which is a
  // deploy fact, and the console has to be able to say that instead of «nothing here». The two
  // `*-not-found` codes are the lookup-miss family this table already has a rule for, and
  // `service-profile-conflict` is «act on this before you retry» (the name is taken) — a different
  // fact from a malformed body, so it keeps its own word.
  'subscriber-disabled',
  'subscriber-not-found',
  'service-profile-not-found',
  'service-profile-conflict',
  // S4 (G-SUB-05) — the subscriber's lifecycle step's own refusal. It is NOT a lookup
  // miss (`subscriber-not-found`: the row IS there) and NOT a malformed request
  // (`reference-invalid`: these two doors have no body at all) — it is the ROW's own
  // state that forbids the step, and the operator's next step is to read the row.
  'subscriber-state-conflict',
  // ALM-05 (G-ALM-05) — a cancel naming a window nobody planned. Its own word
  // rather than `device-not-found`'s: a window is a plan, not a device row, and
  // one code for two different misses would make «which lookup failed?»
  // unanswerable from the wire.
  'maintenance-not-found',
  // N5 (G-PRC-10) — the incident doors keep the same discipline. `incident-not-found`
  // is the lookup-miss family; `incident-already-closed` mirrors
  // `alarm-already-cleared` («the closure stands»); `alarm-already-in-incident` is
  // NOT a lookup miss — the alarm exists and already has a home, so the remedy is
  // the OTHER incident, not a retry.
  'incident-not-found',
  'incident-already-closed',
  'alarm-already-in-incident',
  // N6 (G-ALM-08, webhook first) — the notification channels refuse BY NAME.
  // `channel-kind-unsupported` is the owner-ruling sentence (no SMS panel or
  // Telegram bot has been chosen; nothing is invented until it is);
  // `notification-url-invalid` names the http(s) scheme rule.
  'notification-channel-not-found',
  'channel-kind-unsupported',
  'notification-url-invalid',
] as const;

export type ProblemCode = (typeof PROBLEM_CODES)[number];

export interface Problem {
  type: string;
  title: string;
  status: number;
  detail: string;
  code: ProblemCode;
  instance?: string;
  /** Extensions are allowed by RFC 9457 (e.g. adapter diagnostics). */
  [extra: string]: unknown;
}

/* ------------------------------------------------------------------ */
/* Health — `components/schemas/Health`                                */
/* ------------------------------------------------------------------ */

export interface Health {
  service: 'acs-api';
  version: string;
  status: 'ok';
  store_backend: 'memory' | 'mongo';
  nbi_mode: 'canned' | 'http';
  contract_version: 'v1';
  time: string;
}

/* ------------------------------------------------------------------ */
/* Binding — `BindingRequest` / `BindingResponse`                      */
/* ------------------------------------------------------------------ */

export interface BindingRequest {
  acs_device_id: string;
  nas_ref: string;
  subscriber_ref?: string | null;
  org_ref?: string | null;
  asset_ref?: string | null;
  policy_refs?: string[];
}

export interface BindingResponse {
  bound: true;
  binding_version: number;
}

/* ------------------------------------------------------------------ */
/* Devices — `DeviceSummary` / `DevicesListResponse`                   */
/* ------------------------------------------------------------------ */

export interface DeviceSummary {
  acs_device_id: string;
  nas_ref: string | null;
  serial: string;
  oui: string;
  product_class: string;
  manufacturer: string;
  model: string;
  software_version: string;
  online: boolean;
  last_inform_at: string | null;
  binding_version: number;
  stale: boolean;
}

export interface DevicesListResponse {
  items: DeviceSummary[];
  next_cursor: string | null;
}

/** Query for `GET /integration/v1/devices` (server: limit 1..200, default 200). */
export interface ListDevicesQuery {
  online?: boolean;
  manufacturer?: string;
  model?: string;
  cursor?: string;
  limit?: number;
}

/* ------------------------------------------------------------------ */
/* Parameters — `ParameterItem` / `ParamsResponse`                     */
/* ------------------------------------------------------------------ */

export interface ParameterItem {
  path: string;
  value: unknown;
  type: string;
  writable: boolean;
  captured_at: string | null;
}

export interface ParamsResponse {
  items: ParameterItem[];
  next_cursor: string | null;
  source_version: string;
  stale: boolean;
}

export interface ListParamsQuery {
  path?: string;
  cursor?: string;
  limit?: number;
}

/* ------------------------------------------------------------------ */
/* Stations — `StationItem` / `StationsResponse`                        */
/* ------------------------------------------------------------------ */

export interface StationItem {
  mac: string;
  ip: string | null;
  hostname: string | null;
  ssid_instance: number | null;
  seen_at: string;
}

export interface StationsResponse {
  items: StationItem[];
  next_cursor: string | null;
  stale: boolean;
}

export interface ListStationsQuery {
  cursor?: string;
  limit?: number;
}

/* ------------------------------------------------------------------ */
/* Actions — `ActionRequest` / `ActionResponse` / `Job`                */
/* ------------------------------------------------------------------ */

export const ACTION_KINDS = [
  'apply_policy',
  'read_snapshot',
  'reboot',
  'factory_reset',
  'download',
  'rotate_credential',
] as const;
export type ActionKind = (typeof ACTION_KINDS)[number];

/** `ActionRequest.reason` enum. */
export const ACTION_REASONS = ['operator', 'heal', 'incident'] as const;
export type ActionReason = (typeof ACTION_REASONS)[number];

export interface ActionRequest {
  action: ActionKind;
  policy_ref?: string | null;
  desired_state?: Record<string, unknown>;
  reason?: ActionReason;
  maintenance_window?: string | null;
}

export interface ActionResponse {
  job_id: string;
  status: string;
}

/** Job lifecycle as produced by the façade (`queued|running|completed|failed`). */
export const JOB_STATUSES = ['queued', 'running', 'completed', 'failed'] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

export interface Job {
  job_id: string;
  action: string;
  status: string;
  result: unknown;
  started_at: string | null;
  completed_at: string | null;
  requested_at: string | null;
}

/* ------------------------------------------------------------------ */
/* Diagnostics — `DiagnosticsRequest` (`speedtest` needs tr143)        */
/* ------------------------------------------------------------------ */

export const DIAGNOSTICS_TYPES = ['ping', 'traceroute', 'speedtest'] as const;
export type DiagnosticsType = (typeof DIAGNOSTICS_TYPES)[number];

export interface DiagnosticsRequest {
  type: DiagnosticsType;
  target: string;
  params?: Record<string, unknown>;
}

/* ------------------------------------------------------------------ */
/* Events — `EventReadResponse` + CloudEvents 1.0 envelope             */
/* ------------------------------------------------------------------ */

export interface EventReadResponse {
  items: CloudEvent[];
  next_cursor: string | null;
}

export interface ListEventsQuery {
  source?: string;
  cursor?: string;
  limit?: number;
}

/** CloudEvents 1.0 envelope (`schemas/cloudevents/envelope.json`). */
export interface CloudEvent<T extends object = Record<string, unknown>> {
  specversion: '1.0';
  id: string;
  source: string;
  /** `^io\.wicore\.acs\.` + kind + `.v1` */
  type: string;
  time: string;
  datacontenttype?: string;
  data: T;
}

/** `acs_device_id` is injected into every ACS event data block. */
export interface EventDataBase {
  acs_device_id?: string;
}

export interface OnlineStatusData extends EventDataBase {
  online: boolean;
  last_seen_at: string;
}

export interface DiscoveredData extends EventDataBase {
  serial: string;
  oui: string;
  product_class: string;
}

export interface BoundData extends EventDataBase {
  nas_ref: string;
}

export interface OnuLosData extends EventDataBase {
  olt_ref: string;
  pon_port: string;
  alarm: string;
  severity: string;
  cleared: boolean;
  occurred_at: string;
}

export interface OpticalDegradedData extends EventDataBase {
  olt_ref: string;
  pon_port: string;
  rx_dbm: number | null;
  tx_dbm: number | null;
  threshold_dbm: number;
  sample_count: number;
  sampled_at: string;
}

export interface CampaignProgressData extends EventDataBase {
  campaign_id: string;
  wave: string;
  phase: string;
  from_version: string | null;
  to_version: string | null;
  outcome: string;
  devices_total: number;
  devices_succeeded: number;
  devices_failed: number;
  error_rate: number;
}

export type EventTypeKind =
  | 'device.discovered'
  | 'device.bound'
  | 'device.inform'
  | 'device.online_status'
  | 'device.parameter.changed'
  | 'device.command.completed'
  | 'device.config.drift'
  | 'device.firmware.updated'
  | 'device.factory_reset'
  | 'onu.authorized'
  | 'onu.los'
  | 'optical.degraded'
  | 'diagnostics.completed'
  | 'provision.completed'
  | 'campaign.progress'
  | 'reconciliation.mismatch'
  | 'device.replaced';

/** `io.wicore.acs.<kind>.v1` — the exact type the façade emits. */
export function eventTypeUri(kind: EventTypeKind): string {
  return `io.wicore.acs.${kind}.v1`;
}

/* ------------------------------------------------------------------ */
/* Activations / replace — `/ops/v1` (v1.1 additive)                    */
/* ------------------------------------------------------------------ */

export interface ActivationRequest {
  serial: string;
  olt_id: string;
  pon_port: string;
  line_profile: string;
  service_profile: string;
  vlan?: number | null;
  subscriber_ref?: string | null;
}

export interface ActivationAccepted {
  activation_id: string;
  status: 'await_inform';
}

export const ACTIVATION_STATUSES = [
  'await_inform',
  'informed',
  'bound',
  'provisioned',
  'failed',
] as const;
export type ActivationStatusValue = (typeof ACTIVATION_STATUSES)[number];

export interface ActivationStep {
  step: string;
  status: string;
  detail?: string;
  [extra: string]: unknown;
}

/**
 * The three adapter steps a ZTP activation records, in the order the façade
 * runs them. Mirrors `_run_activation()` in `acs_service/routers/ops.py` and the
 * `ActivationStatus.steps` description in `openapi/openapi.yaml`
 * ("authorize, assign-profile, push-tr069; plus deauthorize on device replace").
 * The wire is free text, so this is the console's *expected* plan, not a filter:
 * a step the API returned that is not listed here is still rendered.
 */
export const ACTIVATION_STEP_NAMES = [
  'authorize',
  'assign-profile',
  'push-tr069',
  'deauthorize',
] as const;
export type ActivationStepName = (typeof ACTIVATION_STEP_NAMES)[number];

export interface ActivationStatus {
  activation_id: string;
  status: ActivationStatusValue;
  steps: ActivationStep[];
  acs_device_id?: string;
  error?: { step: string; code: string; detail: string };
}

export interface ReplaceRequest {
  new_serial: string;
  olt_id: string;
  pon_port: string;
  line_profile: string;
  service_profile: string;
  vlan?: number | null;
  reason?: string | null;
}

export interface ReplaceAccepted {
  activation_id: string;
  old_acs_device_id: string;
  status: 'await_inform';
}

/* ------------------------------------------------------------------ */
/* Alarms — `/ops/v1/alarms` (v1.1 additive, coverage §2)              */
/* ------------------------------------------------------------------ */

/**
 * `Alarm.severity` — the OpenAPI `Alarm.severity` enum, which is
 * `ALARM_SEVERITIES = ("critical", "warning", "info")` in
 * `acs_service/models.py`. The mapping event-kind → severity is a fixed table
 * server-side (`SEVERITY_BY_TYPE` in `acs_service/routers/alarms.py`), so the
 * console never re-derives a severity itself.
 */
export const ALARM_SEVERITIES = ['critical', 'warning', 'info'] as const;
export type AlarmSeverity = (typeof ALARM_SEVERITIES)[number];

/**
 * The four CloudEvents kinds the feed is derived from (`Alarm.type` enum in
 * `openapi/openapi.yaml`, from `SEVERITY_BY_TYPE`). Kept as the console's
 * *expectation*, not a filter: an unknown type still renders (the wire types it
 * as a plain string), so a new trap kind cannot make the feed lose a row.
 */
export const ALARM_EVENT_TYPES = [
  'io.wicore.acs.onu.los.v1',
  'io.wicore.acs.optical.degraded.v1',
  'io.wicore.acs.device.online_status.v1',
  'io.wicore.acs.reconciliation.mismatch.v1',
] as const;
export type AlarmEventType = (typeof ALARM_EVENT_TYPES)[number];

/**
 * `Alarm.state` — the lifecycle alphabet (`ALARM_STATES` in
 * `acs_service/models.py`): `open` → `acked` → `cleared`. `cleared` is
 * TERMINAL — a closed record moves no more, and the doors that would move it
 * again refuse with their own words.
 */
export const ALARM_STATES = ['open', 'acked', 'cleared'] as const;
export type AlarmState = (typeof ALARM_STATES)[number];

/**
 * One alarm row, MATERIALIZED server-side (N1, G-ALM-01): it lives in the
 * `acs_alarms` collection from the moment its event is enqueued, so
 * `alarm_id` IS the CloudEvents id (the same key
 * `GET /integration/v1/events` names) and `data` is that event's payload
 * verbatim. `acked` is the stored acknowledgement fact (a human acknowledged
 * it), and `state`/`escalated`/`assignee` are the lifecycle the doors move.
 */
export interface Alarm {
  alarm_id: string;
  /** `io.wicore.acs.<kind>.v1` of the event this row was derived from. */
  type: string;
  severity: AlarmSeverity;
  acs_device_id: string;
  time: string;
  data: Record<string, unknown>;
  /**
   * «was this alarm ACKNOWLEDGED» — the `:ack` door's fact, and the same
   * question the `alarm.ack` ledger answered before the alarm store existed.
   * It is NOT «the row is no longer open»: an alarm closed straight from
   * `open` was never acknowledged, and the `acked` FILTER counts
   * acknowledgements. The lifecycle is `state`.
   */
  acked: boolean;
  /** where the row is in its lifecycle: `open` → `acked` → `cleared`. */
  state: AlarmState;
  /**
   * N3 (G-ALM-04): the cause-within-the-kind the dedup key names — the
   * crossed threshold for optical.degraded, the ONU serial for onu.los,
   * the empty string for kinds with no sub-cause. Optional because rows
   * born before N3 carry none of these facets.
   */
  fingerprint?: string;
  /**
   * N3 (G-ALM-04): how many same-key events the flap window collapsed onto
   * this row. The server defaults it to 1; absent on pre-N3 rows.
   */
  occurrences?: number;
  /**
   * N3 (G-ALM-04): the newest same-key event's own time — the window is
   * measured from here, and `time` stays the birth.
   */
  last_seen_at?: string | null;
  /** the alarm was raised for attention (a facet, not a state). */
  escalated: boolean;
  /** who holds the alarm now; null when nobody does. */
  assignee: string | null;
}

export interface AlarmsResponse {
  items: Alarm[];
  next_cursor: string | null;
}

/**
 * Query for `GET /ops/v1/alarms`. `device` is an EXACT `acs_device_id` match
 * (server-side, not a substring), `acked` is tri-state (omitted = both), and an
 * unknown `severity` is answered 422 `reference-invalid` — never silently
 * ignored.
 */
export interface ListAlarmsQuery {
  severity?: AlarmSeverity | '';
  device?: string;
  acked?: boolean;
  cursor?: string;
  limit?: number;
}

/** `POST /ops/v1/alarms/{event_id}:ack` body — free text, `maxLength: 500`. */
export interface AlarmAckRequest {
  note?: string | null;
}

/**
 * The ack answer. `acked` is `const: true` in the contract; `already_acked`
 * says this client had already acked the same alarm, so no second audit entry
 * was written (the ledger dedupes per alarm + client).
 */
export interface AlarmAckResponse {
  alarm_id: string;
  acked: true;
  already_acked: boolean;
  note: string | null;
  /** `client_id` of the acking client, as recorded in the audit ledger. */
  acked_by: string;
}

/* -- the lifecycle doors (N2) — `/ops/v1/alarms/{event_id}:clear|:escalate|:assign` -- */

/** `POST /ops/v1/alarms/{event_id}:clear` body — free text, `maxLength: 500`. */
export interface AlarmClearRequest {
  note?: string | null;
}

/**
 * The close answer. `already_cleared` says THIS person had already closed the
 * alarm (the replay wrote no second audit entry); another operator's closure
 * is refused with 409 `alarm-already-cleared` instead.
 */
export interface AlarmClearResponse {
  alarm_id: string;
  state: string;
  cleared_by: string;
  already_cleared: boolean;
  note: string | null;
}

/** `POST /ops/v1/alarms/{event_id}:escalate` body — free text, `maxLength: 500`. */
export interface AlarmEscalateRequest {
  note?: string | null;
}

/** The escalation answer; escalation is a flag, so a repeat is `already_escalated`. */
export interface AlarmEscalateResponse {
  alarm_id: string;
  /** read from the wire — the contract says `const: true` today, and the console
   * shows what the server said rather than what it expects to hear. */
  escalated: boolean;
  already_escalated: boolean;
  escalated_by: string;
  note: string | null;
}

/** `POST /ops/v1/alarms/{event_id}:assign` body — who takes the alarm. */
export interface AlarmAssignRequest {
  assignee: string;
  note?: string | null;
}

/** The hand-over answer; both names are in it, so the change is visible. */
export interface AlarmAssignResponse {
  alarm_id: string;
  assignee: string;
  previous_assignee: string | null;
  assigned_by: string;
  note: string | null;
}

/* ------------------------------------------------------------------ */
/* Maintenance windows (ALM-05) — `/ops/v1/maintenance`                */
/* ------------------------------------------------------------------ */

/** `MAINTENANCE_SCOPES` in `acs_service/models.py` — no `fleet`: a fleet-wide
 *  window is a silencer with no device vocabulary. */
export const MAINTENANCE_SCOPES = ['device', 'olt'] as const;
export type MaintenanceScope = (typeof MAINTENANCE_SCOPES)[number];

/** `MAINTENANCE_STATES` — born `active`, moved only by the cancel door. There
 *  is deliberately no `expired`: a window whose end passed is still `active`
 *  as a row and simply never matches again. */
export const MAINTENANCE_STATES = ['active', 'cancelled'] as const;
export type MaintenanceState = (typeof MAINTENANCE_STATES)[number];

/**
 * One planned quiet night. `suppressed` is the window's own honesty counter —
 * how many alarm rows the raise path ate while this window covered the event's
 * OWN time — and every transition names a person.
 */
export interface MaintenanceWindow {
  window_id: string;
  scope: MaintenanceScope;
  target: string;
  starts_at: string;
  ends_at: string;
  note: string | null;
  state: MaintenanceState;
  created_at: string;
  created_by: string;
  cancelled_at: string | null;
  cancelled_by: string | null;
  suppressed: number;
}

/** `GET /ops/v1/maintenance` — the door is OFFSET-paged (not cursor), newest
 *  first by `created_at`; `total` counts the filter over the whole collection. */
export interface MaintenanceListResponse {
  items: MaintenanceWindow[];
  total: number;
}

export interface ListMaintenanceQuery {
  state?: MaintenanceState | '';
  offset?: number;
  limit?: number;
}

/** `POST /ops/v1/maintenance` — `window` is one ISO-8601 UTC interval
 *  `start/end`, the SAME shape the campaign door reads. Idempotency-Key
 *  required; human-only (`actor-not-human` otherwise). */
export interface MaintenanceCreateRequest {
  scope: MaintenanceScope;
  target: string;
  window: string;
  note?: string | null;
}

export interface MaintenanceCreateResponse {
  item: MaintenanceWindow;
}

/** `POST /ops/v1/maintenance/{window_id}:cancel` — CAS: the loser of a race
 *  hears `already_cancelled` with the winner's name, never overwrites it. */
export interface MaintenanceCancelResponse {
  window_id: string;
  state: string;
  already_cancelled: boolean;
  cancelled_by: string | null;
  cancelled_at: string | null;
}

/* ------------------------------------------------------------------ */
/* Incidents (N5) — `/ops/v1/incidents`                                */
/* ------------------------------------------------------------------ */

/** `INCIDENT_STATES` in `acs_service/models.py` — two states, one way: closing
 *  an incident is a decision and a closed incident moves no more. */
export const INCIDENT_STATES = ['open', 'closed'] as const;
export type IncidentState = (typeof INCIDENT_STATES)[number];

/** `INCIDENT_TIMELINE_KINDS` in `acs_service/models.py` — how an entry came to
 *  be; the door refuses any other kind, so the console never invents one. */
export const INCIDENT_TIMELINE_KINDS = ['created', 'note', 'attach', 'close'] as const;
export type IncidentTimelineKind = (typeof INCIDENT_TIMELINE_KINDS)[number];

/** One war-room entry. `actor` is the one string the row and the audit agree
 *  on (the alarms doors' rule), so a timeline entry joins an audit row without
 *  a second vocabulary. */
export interface IncidentTimelineEntry {
  at: string;
  kind: IncidentTimelineKind;
  text: string | null;
  actor: string;
}

/**
 * One incident row — the operator's answer to «these alarms are one story»
 * (N5, P22). The alarms it links are evidence it cites, not rows it owns:
 * closing it closes nothing else. `severity` is derived at creation (the worst
 * of the attached alarms, `info` when none) and `olt_id` is carried only when
 * every attached device shares one OLT — an incident spanning two is honest
 * about spanning them with `null`.
 */
export interface Incident {
  incident_id: string;
  title: string;
  severity: AlarmSeverity;
  state: IncidentState;
  olt_id: string | null;
  created_at: string;
  created_by: string;
  closed_at: string | null;
  closed_by: string | null;
  alarm_ids: string[];
  timeline: IncidentTimelineEntry[];
}

/** `GET /ops/v1/incidents` — OFFSET-paged like the maintenance list, newest
 *  first by `created_at`; `total` counts the state filter. */
export interface IncidentListResponse {
  items: Incident[];
  total: number;
}

export interface ListIncidentsQuery {
  state?: IncidentState | '';
  offset?: number;
  limit?: number;
}

/** One correlate proposal — the RULE never opens an incident; only the
 *  operator's POST does. */
export interface IncidentGroup {
  olt_id: string;
  device_count: number;
  alarm_count: number;
  severity: AlarmSeverity;
  devices: string[];
  alarm_ids: string[];
}

/** `GET /ops/v1/incidents:correlate` — open alarms grouped by the OLT their
 *  devices live on. `unassociated_devices` is the population the rule could
 *  NOT correlate, COUNTED rather than dropped. */
export interface IncidentCorrelateResponse {
  groups: IncidentGroup[];
  unassociated_devices: number;
  scanned: number;
  truncated: boolean;
  as_of: string;
}

export interface ListIncidentCorrelateQuery {
  min_devices?: number;
}

export interface IncidentCreateRequest {
  title: string;
  note?: string | null;
  alarm_ids?: string[];
}

export interface IncidentNoteRequest {
  text: string;
}

export interface IncidentAttachRequest {
  alarm_ids: string[];
}

/** `POST …/:attach` — the dedup is the replay: ids already here come back in
 *  `already_attached` and the call stays 200. */
export interface IncidentAttachResponse {
  incident: Incident;
  attached: string[];
  already_attached: string[];
}

export interface IncidentCloseRequest {
  note?: string | null;
}

/* ------------------------------------------------------------------ */
/* Notification channels (N6) — `/ops/v1/notification-channels`        */
/* ------------------------------------------------------------------ */

/** `NOTIFICATION_CHANNEL_KINDS` in `acs_service/models.py` — `webhook` ONLY.
 *  The row's SMS/Telegram/Email half is an owner ruling nobody has made, and
 *  any other kind answers 422 `channel-kind-unsupported` by name. */
export const NOTIFICATION_CHANNEL_KINDS = ['webhook'] as const;
export type NotificationChannelKind = (typeof NOTIFICATION_CHANNEL_KINDS)[number];

/** `NOTIFICATION_MIN_SEVERITIES` in `acs_service/models.py` — the per-channel
 *  severity FLOOR on the N5 rank (critical > warning > info). */
export const NOTIFICATION_MIN_SEVERITIES = ['critical', 'warning', 'info'] as const;
export type NotificationMinSeverity = (typeof NOTIFICATION_MIN_SEVERITIES)[number];

/**
 * One configured webhook channel. The secret is WRITE-ONLY: the row answers
 * `has_secret` and never echoes it — so the console can say a secret exists
 * without ever holding one.
 */
export interface NotificationChannel {
  channel_id: string;
  kind: NotificationChannelKind;
  name: string;
  url: string;
  min_severity: NotificationMinSeverity;
  enabled: boolean;
  has_secret: boolean;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface NotificationChannelCreateRequest {
  kind?: NotificationChannelKind;
  name: string;
  url: string;
  min_severity?: NotificationMinSeverity;
  enabled?: boolean;
  /** write-only: no response echoes it. */
  secret?: string;
}

/** Partial update — only the keys present are applied (the server filters
 *  `None`s); `kind` is not updatable, a webhook stays a webhook. */
export interface NotificationChannelUpdateRequest {
  name?: string | null;
  url?: string | null;
  min_severity?: NotificationMinSeverity | null;
  enabled?: boolean | null;
  /** write-only: replacing it needs the new value; clearing it is `""`. */
  secret?: string | null;
}

export interface NotificationChannelListResponse {
  items: NotificationChannel[];
  total: number;
}

/** `DELETE …/{channel_id}` — intents it leaves pending fail closed in the
 *  worker with `channel-absent`. */
export interface NotificationChannelDeletedResponse {
  deleted: boolean;
  channel_id: string;
}

/** `NOTIFICATION_DELIVERY_STATUSES` in `acs_service/models.py` — one intent's
 *  lifecycle: born `pending` at the raise point, closed by the notify worker. */
export const NOTIFICATION_DELIVERY_STATUSES = ['pending', 'delivered', 'failed'] as const;
export type NotificationDeliveryStatus = (typeof NOTIFICATION_DELIVERY_STATUSES)[number];

/**
 * One delivery intent and its outcome. A `pending` intent is a promise — a
 * real one the worker will close — and a terminal row carries `finished_at`
 * and (when it failed) a `last_error` that is a house code plus the
 * exception's TYPE, never its text.
 */
export interface NotificationDelivery {
  delivery_id: string;
  channel_id: string;
  channel_name: string;
  kind: 'alarm' | 'test';
  alarm_id: string | null;
  alarm_type: string;
  severity: NotificationMinSeverity | '';
  acs_device_id: string;
  occurred_at: string;
  status: NotificationDeliveryStatus;
  attempts: number;
  last_error: string | null;
  next_attempt_at: string | null;
  created_at: string;
  finished_at: string | null;
}

export interface NotificationDeliveryListResponse {
  items: NotificationDelivery[];
  total: number;
}

export interface ListNotificationDeliveriesQuery {
  channel_id?: string;
  limit?: number;
}

/* ------------------------------------------------------------------ */
/* OLT views + optical — `/ops/v1` (v1.1 additive, coverage §3)        */
/* ------------------------------------------------------------------ */

/**
 * One adapter inventory row (`GET /ops/v1/olts`), enriched server-side with
 * `onu_count`. `note` is set when THIS OLT's ONU table could not be read: the
 * count is then 0 but the row says why — an unreadable OLT is not an empty one.
 */
export interface OltInventoryItem {
  olt_id: string;
  brand: string;
  host: string;
  enabled: boolean;
  onu_count: number;
  note: string | null;
}

/** The adapter returns the whole inventory, so there is nothing to page. */
export interface OltListResponse {
  items: OltInventoryItem[];
  total: number;
}

/** The `tr069` block the canned drivers write on `push-tr069`. */
export interface OnuTr069 {
  acs_url?: string;
  inform_interval?: number;
  pushed_at?: string;
  [extra: string]: unknown;
}

/**
 * One row of `GET /ops/v1/olts/{olt_id}/onus`. The contract types the items as
 * free objects (`OnuTableResponse.items: list[dict]` in `models.py`) because
 * this is a passthrough of the adapter's SNMP-backed row set, so only `sn` is
 * required and unknown keys survive (`[extra: string]: unknown`) instead of
 * being dropped. Known keys are the canned driver's vocabulary
 * (`olt_adapter/drivers/base.py`): `status` `registered|unregistered`,
 * `auth_state` `authorized|unauthorized`.
 */
export interface OnuTableRow {
  sn: string;
  olt_id?: string;
  pon_port?: string;
  status?: string;
  auth_state?: string;
  profile?: string | null;
  line_profile?: string | null;
  service_profile?: string | null;
  vlan?: string | number | null;
  tr069?: OnuTr069 | null;
  [extra: string]: unknown;
}

export interface OnuTableResponse {
  olt_id: string;
  items: OnuTableRow[];
  total: number;
}

/** Adapter-side status filter of `GET …/onus` (e.g. `registered`). */
export interface ListOltOnusQuery {
  status?: string;
}

/**
 * One PON port measured against the DECLARED capacity (V7, G-PRC-08). `capacity` is the
 * operator's declaration (`settings:pon_capacity_onus_per_port`), never a device reading;
 * `remaining` may be NEGATIVE past the declaration — the overflow is the signal, not a
 * value to clamp.
 */
export interface PonCapacityRow {
  pon_port: string;
  onu_count: number;
  capacity: number;
  utilization_pct: number;
  remaining: number;
  at_capacity: boolean;
}

/**
 * `GET /ops/v1/olts/{olt_id}/pon-capacity`. `ports` keeps the raw table door's null
 * discipline: null = nobody read the table, [] = the table really mentions no port. The
 * population counts are of the WHOLE table. No fill forecast and no uplink capacity —
 * neither has a data source in this deployment.
 */
export interface PonCapacityResponse {
  olt_id: string;
  declared_capacity: number;
  capacity_source: string;
  ports: PonCapacityRow[] | null;
  ports_counted: number;
  total_onus: number;
  ports_at_capacity: number;
  unknown_fields?: string[];
  note?: string | null;
}

/**
 * `POST /ops/v1/olts/{olt_id}/onus:authorize` — the DIRECT manual authorize
 * (also the repair/retry path). `sn` carries
 * `^[A-Za-z0-9._-]{6,32}$` and `pon_port` `min_length=1` (`OnuAuthorizeRequest`);
 * `line_profile`/`service_profile` are optional so the emitted `onu.authorized`
 * event can carry them instead of inventing a key.
 */
export interface OnuAuthorizeRequest {
  sn: string;
  pon_port: string;
  profile?: string | null;
  line_profile?: string | null;
  service_profile?: string | null;
}

export interface OnuAuthorizeResponse {
  olt_id: string;
  sn: string;
  pon_port: string;
  admin_state: string;
  authorized: boolean;
}

/**
 * `window` of the two optical doors (`OPTICAL_WINDOWS` in
 * `acs_service/routers/olt_ops.py`). `…/optical` answers ONE live sample for it;
 * `…/optical/history` (V3) answers the stored series over exactly that range, so
 * a trend is drawn from recorded readings and never from the single sample.
 */
export const OPTICAL_WINDOWS = ['1h', '24h', '7d', '30d'] as const;
export type OpticalWindow = (typeof OPTICAL_WINDOWS)[number];

/**
 * The bucket width the history door reports each window at — the same table the
 * server publishes in `acs_service/optical.py::HISTORY_BUCKETS`. It is spelled
 * here as a literal because the panel sizes its axis with it; the parity guard in
 * `api/contract.test.ts` compares these four numbers with the document, so a
 * silent drift between the two tables is red, not a wrong-looking chart.
 */
export const OPTICAL_BUCKET_SECONDS: Record<OpticalWindow, number> = {
  '1h': 300,
  '24h': 900,
  '7d': 3600,
  '30d': 14400,
} as const;

/** The length of each published window in seconds (`window` is the range, not just the label). */
export const OPTICAL_WINDOW_SECONDS: Record<OpticalWindow, number> = {
  '1h': 3600,
  '24h': 86400,
  '7d': 604800,
  '30d': 2592000,
} as const;

/** One instantaneous RX/TX sample proxied from the adapter. */
export interface OpticalRead {
  rx_dbm: number | null;
  tx_dbm: number | null;
  measured_at: string;
  olt_id: string;
  sn: string;
}

/**
 * One NON-EMPTY bucket of the stored series. A bucket with no sample is absent from
 * the array — never a point at zero, which would be a reading nobody took.
 */
export interface OpticalHistoryPoint {
  at: string;
  rx_dbm: number;
  tx_dbm: number | null;
  samples: number;
}

/** `GET /ops/v1/devices/{id}/optical/history` (V3, G-TLM-02). */
export interface OpticalHistory {
  acs_device_id: string;
  olt_id: string | null;
  window: OpticalWindow;
  window_start: string;
  window_end: string;
  bucket_seconds: number;
  points: OpticalHistoryPoint[];
  samples: number;
  last_sampled_at: string | null;
  note: string;
}

/**
 * The published envelope of `limit` on the fleet's worst list (`WORST_LIMIT_DEFAULT` /
 * `WORST_LIMIT_MAX` in `acs_service/optical.py`). Spelled as literals because the panel
 * sizes itself with them and `api/contract.test.ts` compares both numbers with the document.
 */
export const WORST_LIMIT_DEFAULT = 25;
export const WORST_LIMIT_MAX = 100;

/** One histogram bin in dBm (`RX_BIN_DBM`) — the precision the OLT reports at. */
export const RX_BIN_DBM = 1;

/**
 * One device in the fleet's worst list (V3b). Ranked by `rx_min_dbm`: the darkest reading the
 * sweep stored in the window, not the mean — the ONU that dips once is the one that drops a call.
 */
export interface OpticalWorstDevice {
  acs_device_id: string;
  rx_min_dbm: number;
  rx_avg_dbm: number | null;
  samples: number;
  last_sampled_at: string | null;
}

/** One OCCUPIED dBm bin of the per-device minima. An empty bin is absent, never a bar at zero. */
export interface OpticalRxBin {
  bin_dbm: number;
  devices: number;
}

/**
 * `GET /ops/v1/optical/worst` (V3b, G-TLM-03). `devices`, `samples` and `histogram` describe the
 * whole rolled-up fleet; only `worst` is cut at `limit`. A fleet nobody measured is `devices: 0`
 * with empty lists — a real answer, not a failure.
 */
export interface OpticalWorst {
  window: OpticalWindow;
  window_start: string;
  window_end: string;
  limit: number;
  devices: number;
  samples: number;
  bin_dbm: number;
  worst: OpticalWorstDevice[];
  histogram: OpticalRxBin[];
  note: string;
}

/**
 * The two windows the decline door publishes (`DEGRADING_WINDOWS` in
 * `acs_service/optical.py`, plan ruling D5). A rate in dBm/day taken from two hours of data is a
 * guess wearing a unit, so this door is deliberately narrower than its two neighbours — the panel's
 * `select` must not offer an option the door answers with a 422.
 */
export const DEGRADING_WINDOWS = ['7d', '30d'] as const;
export type OpticalDegradingWindow = (typeof DEGRADING_WINDOWS)[number];

/** Days beyond which the countdown is `null`: past this the answer is «we do not know» (D2). */
export const DEGRADING_HORIZON_DAYS = 90;

/** Readings EACH half must hold before a rate is published (D2) — the guard the panel names aloud. */
export const DEGRADING_MIN_SAMPLES_PER_HALF = 3;

/**
 * One ONU whose newer half reads lower than its older half (V3c).
 *
 * Both means travel with the rate so the reader can redo the arithmetic, and the two sample counts
 * are the guard's evidence rather than decoration. `days_to_warn` is null in exactly two cases and
 * the two flags say which — a device already past the line gets no countdown, and a countdown past
 * the horizon is not knowledge.
 */
export interface OpticalDegradingDevice {
  acs_device_id: string;
  rx_avg_first: number;
  rx_avg_second: number;
  rx_min_second: number | null;
  samples_first: number;
  samples_second: number;
  rate_dbm_per_day: number;
  days_to_warn: number | null;
  within_horizon: boolean;
  already_below_warn: boolean;
  last_sampled_at: string | null;
}

/**
 * `GET /ops/v1/optical/degrading` (V3c, G-TLM-03) — the fleet's decline rates.
 *
 * The one optical door that answers about the future, and therefore the one that consumes the
 * operator's stored thresholds and prints them: a countdown measured against an unnamed line cannot
 * be checked. `devices`, `samples` and `insufficient` describe the rolled-up fleet and never shrink
 * with `limit`.
 */
export interface OpticalDegrading {
  window: OpticalDegradingWindow;
  window_start: string;
  window_end: string;
  half_seconds: number;
  limit: number;
  devices: number;
  samples: number;
  insufficient: number;
  warn_rx_dbm: number;
  critical_rx_dbm: number;
  horizon_days: number;
  min_samples_per_half: number;
  declining: OpticalDegradingDevice[];
  note: string;
}

/** The remaining tag list after a `PUT`/`DELETE …/tags/{tag}`. */
export interface TagsResponse {
  tags: string[];
}

/* ------------------------------------------------------------------ */
/* Firmware registry — `/ops/v1/firmware…` (circuit E)                  */
/* ------------------------------------------------------------------ */

/**
 * `FirmwareFile` — one registry record. Metadata only: the image bytes live in
 * GenieACS file storage and are served to the CPE by genieacs-fs, so no endpoint
 * on this surface ever returns them.
 */
export interface FirmwareFile {
  file_id: string;
  brand: string;
  model: string;
  version: string;
  /**
   * Since E2 (G-FW-02) this is the digest the service MEASURED over the bytes that
   * arrived, not the string the caller typed. A row here that disagrees with the
   * file is impossible by construction — the door refused it with 422
   * `checksum-mismatch`.
   */
  checksum_sha256: string;
  size_bytes: number;
  status: string;
  genieacs_file: string;
  created_at: string | null;
}

export interface FirmwareListResponse {
  items: FirmwareFile[];
  next_cursor: string | null;
}

/** `FirmwareUploadAccepted` — what either registration door answers. */
export interface FirmwareUploadAccepted {
  file_id: string;
  brand: string;
  model: string;
  version: string;
  status: string;
}

/** `DeletedResponse` — `deleted` is `{ const: true }` in the contract. */
export interface DeletedResponse {
  deleted: true;
}

export interface ListFirmwareQuery {
  brand?: string;
  model?: string;
  cursor?: string;
  limit?: number;
}

/* ------------------------------------------------------------------ */
/* Campaigns — `/ops/v1/campaigns…` (plan P7, circuit E)               */
/* ------------------------------------------------------------------ */

/** `Campaign.status` enum, verbatim. */
export const CAMPAIGN_STATUSES = [
  'pending',
  'running',
  'halted',
  'completed',
  'rolling_back',
] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];

/** What a dispatch pass is: the ladder forward, or one pass back over the population. */
export const CAMPAIGN_KINDS = ['rollout', 'rollback'] as const;
export type CampaignKind = (typeof CAMPAIGN_KINDS)[number];

/** `CampaignNextAction.action` — the six verdicts `campaigns.py::plan` can give. */
export const CAMPAIGN_ACTIONS = [
  'advance',
  'continue_wave',
  'wait_jobs',
  'wait_window',
  'idle',
  'settled',
] as const;
export type CampaignAction = (typeof CAMPAIGN_ACTIONS)[number];

/**
 * `CampaignWindow.status`. `passed` is its own word because it is its own instruction:
 * a window that already closed will not open by waiting, so the campaign needs a human
 * decision (`:resume`, which is allowed and recorded), not another pass.
 */
export const CAMPAIGN_WINDOW_STATUSES = ['waiting', 'open', 'passed'] as const;
export type CampaignWindowStatus = (typeof CAMPAIGN_WINDOW_STATUSES)[number];

export interface CampaignFilter {
  tags?: string[];
  manufacturer?: string | null;
  model?: string | null;
  from_version?: string | null;
}

/** One rung of the ladder: a CUMULATIVE share, strictly increasing, ending at 100. */
export interface CampaignWave {
  pct: number;
}

export interface CampaignProgress {
  queued: number;
  running: number;
  completed: number;
  failed: number;
  /** Jobs recorded so far — devices dispatched, not the population. */
  total: number;
}

export interface CampaignWindow {
  status: CampaignWindowStatus;
  open: boolean;
  starts_at: string;
  ends_at: string;
}

/**
 * `CampaignNextAction` — literally the decision the `acs-campaigns` worker is acting
 * on (`GET` and the worker call the same `campaigns.py::plan`), which is why this is
 * the console's answer to §۴.۱۳'s «why is my wave not moving?».
 */
export interface CampaignNextAction {
  campaign_id: string;
  /** The wave the decision is about — the NEXT one, for `advance`. */
  wave: number;
  kind: CampaignKind;
  status: string;
  window: CampaignWindow | null;
  action: CampaignAction;
  reason: string;
  /** Only for `wait_jobs`. */
  open_jobs?: number;
  /** Only for `advance`/`continue_wave` — what the next pass dispatches. */
  devices?: string[];
}

export interface Campaign {
  campaign_id: string;
  status: CampaignStatus;
  file_id: string;
  brand: string;
  model: string;
  version: string;
  filter: CampaignFilter;
  waves: CampaignWave[];
  /** Computed device count per wave — always sums to the population. */
  per_wave: number[];
  current_wave: number;
  targets: string[];
  /** Devices of the current wave the rate limit has not dispatched. */
  queued: string[];
  population: number;
  queued_devices: number;
  /** Population devices no wave has touched yet — a different backlog from the above. */
  remaining_devices: number;
  progress: CampaignProgress;
  halt_on_fail_pct: number;
  maintenance_window: string | null;
  next_action: CampaignNextAction;
  dispatch_kind: CampaignKind;
  dispatch_file_id: string;
  started_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  /** U-04: operator-profile version active at mint time; null when no profile was active at mint time or the row predates the stamp. */
  profile_version: number | null;
}

/** List view: the detail minus the operator's inputs and the population device ids. */
export interface CampaignListItem {
  campaign_id: string;
  status: CampaignStatus;
  file_id: string;
  brand: string;
  model: string;
  version: string;
  current_wave: number;
  per_wave: number[];
  population: number;
  queued_devices: number;
  remaining_devices: number;
  progress: CampaignProgress;
  started_at: string | null;
  created_at: string | null;
  /** U-04: operator-profile version active at mint time; null when no profile was active at mint time or the row predates the stamp. */
  profile_version: number | null;
}

export interface CampaignListResponse {
  items: CampaignListItem[];
  next_cursor: string | null;
}

export interface ListCampaignsQuery {
  status?: CampaignStatus;
  cursor?: string;
  limit?: number;
}

/** `CampaignWaveInfo` — `devices` counts what THIS request dispatched, not the wave. */
export interface CampaignWaveInfo {
  index: number;
  devices: number;
}

/**
 * `CampaignScheduled` — E4's create answer (G-FW-06). Nothing was dispatched by the
 * request: `wave.devices` is 0 by contract, `queued_devices` is the first rung the
 * campaign worker now owes, and `next_action` is the instruction it acts on.
 */
export interface CampaignScheduled {
  campaign_id: string;
  status: Extract<CampaignStatus, 'pending' | 'completed'>;
  wave: CampaignWaveInfo;
  queued_devices: number;
  next_action: CampaignNextAction;
}

/** `CampaignAccepted` — the create-era answer `:resume` still gives (it dispatches now). */
export interface CampaignAccepted {
  campaign_id: string;
  status: CampaignStatus;
  wave: CampaignWaveInfo;
  /**
   * Only on `:resume`, and only when a declared window is closed: the manual door is
   * where a human overrides the schedule, so the answer says so and the audit row
   * records the same fact.
   */
  dispatched_outside_window?: boolean;
  window?: CampaignWindow | null;
}

export interface CampaignHalted {
  campaign_id: string;
  status: 'halted';
}

export interface CampaignRolledBack {
  campaign_id: string;
  status: CampaignStatus;
  rollback_file_id: string;
  wave: CampaignWaveInfo;
}

export interface CampaignCreateRequest {
  file_id: string;
  filter?: CampaignFilter;
  waves: CampaignWave[];
  halt_on_fail_pct?: number;
  maintenance_window?: string | null;
}

/** A fleet-wide downgrade is never implied: `confirm` must be literally true. */
export interface CampaignRollbackRequest {
  confirm: boolean;
}

/* ------------------------------------------------------------------ */
/* Console-local aggregates                                            */
/* ------------------------------------------------------------------ */

/**
 * Device job history as the FIXTURE aggregate.
 *
 * The door for this is live and wired: `GET /ops/v1/jobs?device&status&type`
 * (`routers/system.py:177`, capability `jobs.read`) — its wire shape is `JobListRow`
 * below, which is what `JobsPanel` renders since U-16-f. This aggregate survives only
 * as the fixture's internal row type (session rows minted by `runAction`/`runDiagnostics`
 * and `fixtures/records.ts::jobsFor`), converted to `JobListRow` at the fixture door; the
 * HTTP provider no longer answers `EndpointPendingError` here.
 */
export interface DeviceJobRow {
  job_id: string;
  action: string;
  status: JobStatus;
  requested_at: string;
  started_at: string | null;
  completed_at: string | null;
  result_summary: string | null;
  error_code?: ProblemCode;
}

/**
 * Device capability view, read off the *device doc*.
 *
 * `POST …/diagnostics` gates `speedtest` on `capabilities: ["tr143", …]` of the
 * device doc (`acs_service/routers/diagnostics.py`), but `DeviceSummary` — the
 * only shape `GET /devices/{id}` answers — has no capabilities field, and the
 * lab-verified capability registry is phase-B1 work. Same honest pattern as
 * `DeviceJobRow`: the fixture provider serves it from the enriched device doc,
 * the HTTP provider fails with `EndpointPendingError` instead of inventing a
 * route, and the diagnostics page then falls back to the API's own fail-closed
 * `action-unsupported` answer.
 */
export interface DeviceCapabilities {
  acs_device_id: string;
  capabilities: string[];
  /** `tr143` listed — the exact gate the API applies to `speedtest`. */
  tr143: boolean;
}

/** Fleet health derived on the client from the device list (the per-brand SAMPLE only — V4). */
export interface FleetHealth {
  total: number;
  online: number;
  offline: number;
  stale: number;
  unbound: number;
  byManufacturer: { manufacturer: string; total: number; online: number }[];
}

/**
 * `GET /ops/v1/stats/devices` — the fleet's counts over the WHOLE store (V4 / G-TLM-05).
 *
 * The dashboard used to reduce the first page of `GET /devices` (200 rows) and print the result as
 * fleet health: numbers that are wrong, not merely incomplete. `offline = total − online`, and
 * `unbound = total − bindings` — exact rather than assumed, because a binding row only ever names
 * a device the bind door found (it answers 404 for an unknown id). `counted_at` is the number's own
 * date: a count without one is a claim about «now» that nobody made.
 */
export interface DeviceStats {
  total: number;
  online: number;
  offline: number;
  unbound: number;
  counted_at: string;
}

/**
 * `GET /ops/v1/stats/alarms` — the lifecycle counts, in the closed vocabularies.
 *
 * `active = open + acked`; `cleared` is the end of the lifecycle, not open work. Both maps cover
 * the whole `ALARM_STATES` / `ALARM_SEVERITIES` alphabet, and `active_by_severity` counts **active**
 * rows only — a cleared critical alarm is history, and counting it as live would be the same class
 * of lie the device door replaced. A zero in either map is measured, not missing.
 */
export interface AlarmStats {
  total: number;
  active: number;
  by_state: Record<AlarmState, number>;
  active_by_severity: Record<AlarmSeverity, number>;
  counted_at: string;
}

/* ------------------------------------------------------------------ */
/* Human identity — `components/schemas/Session*`, `EffectivePermissions` */
/* ------------------------------------------------------------------ */

/**
 * The capability vocabulary of `GET /ops/v1/permissions/effective`
 * (`docs/RBAC_AND_CONSOLE_FA.md` §۱/§۴). The wire only ever says `read` or `edit` —
 * anything absent is `none` — and `unknown` is NOT a server answer: it is the
 * console's own admission that the mirror has not arrived yet (U-08-f / D10).
 * Collapsing `unknown` into `none` would render "I do not know" as "you are not
 * allowed", which is the `null`-vs-zero mistake U-06-e recorded.
 */
export const CAPABILITY_WIRE_LEVELS = ['read', 'edit'] as const;
export type CapabilityWire = (typeof CAPABILITY_WIRE_LEVELS)[number];
export type Capability = CapabilityWire | 'none';
export type CapabilityLevel = Capability | 'unknown';

/** The four roles §۲ publishes; read from the server's `roles`, never hard-coded here. */
export const RBAC_ROLES = ['owner', 'admin', 'operator', 'user'] as const;

/** Body of `POST /ops/v1/sessions` — the console's login, and the only two fields it has. */
export interface SessionRequest {
  username: string;
  password: string;
}

/**
 * `POST /ops/v1/sessions` → a short-lived session JWT plus what it can do.
 *
 * `token` is the ONLY credential the browser ever holds, and it stays in module
 * memory (`api/session.ts`): no Web Storage — neither the persistent nor the per-tab
 * flavour — and no cookie (invariant ۴). There is no username field, which is why the identity
 * badge reads the name off `effective().subject` — the server's own answer — instead of
 * trusting what was typed.
 */
export interface SessionGranted {
  token: string;
  role: string;
  expires_at: string;
  sid: string;
  capabilities: Record<string, CapabilityWire>;
  /**
   * R8: `true` when the password step succeeded but this account's second factor is still
   * outstanding — the answer that sends the console to `POST /ops/v1/sessions/totp`.
   *
   * That answer carries no session: the façade serialises the same `SessionGranted` shape with
   * `token`, `role` and `sid` empty rather than omitting them (the published schema has no
   * `required` list), so `token` is the empty string there and `api/session.ts` reads an empty
   * token as «no credential» instead of handing `Bearer ` to the transport. Absent (or `false`)
   * is the one-step login every account without an enrolled factor still gets.
   */
  mfa_required?: boolean;
  /**
   * `mfa:<32 hex>` — the opaque single-use value the second step spends. Not a JWT, and no door
   * accepts it as a credential; the console keeps it only for the length of the outstanding step.
   */
  challenge?: string | null;
  /** When the challenge stops being spendable (server clock, ISO-8601). */
  challenge_expires_at?: string | null;
}

/** `POST /ops/v1/sessions/totp` — the second step: one challenge and the six digits it asks for. */
export interface TotpSessionRequest {
  challenge: string;
  code: string;
}

/** `DELETE /ops/v1/sessions/current` → the session that stopped working, and when. */
export interface SessionRevoked {
  sid: string;
  revoked_at: string;
  detail: string;
}

/** `GET /ops/v1/permissions/effective` → this caller's access, resolved server-side (§۹). */
export interface EffectivePermissions {
  kind: 'human' | 'machine';
  subject: string;
  role: string | null;
  capabilities: Record<string, CapabilityWire>;
  roles: string[];
}

/* ------------------------------------------------------------------ */
/* audit ledger — `GET /ops/v1/audit` (R7, U-16-d)                      */
/* ------------------------------------------------------------------ */

/**
 * One entry of the operations audit ledger, VERBATIM.
 *
 * This is deliberately `Record<string, unknown>` and not an interface: the door publishes the stored
 * record exactly as the operation wrote it (`acs_service/routers/audit.py`: «VERBATIM, NOT SUMMARISED
 * … a router that starts recording a new key shows up here without a contract change»), and the
 * ledger holds five different actor shapes at once (پیوست ب of
 * `docs/plans/2026-10-17-u16-console-workflows-and-audit-actors.md`). Typing it would be the console
 * claiming a closed field set the server never published — and the append-only half of that claim is
 * simply false, because rows written before U-16-b are still in there.
 *
 * `lib/auditActor.ts` is what reads one, and it branches on the row's `kind` before its key names.
 */
export type AuditEntry = Record<string, unknown>;

/** `GET /ops/v1/audit` → one page of the ledger, the filtered total, the cursor and the honesty note. */
export interface AuditListResponse {
  entries: AuditEntry[];
  /** Rows matching THIS filter across the whole ledger, not just this page. */
  total: number;
  next_cursor: string | null;
  /**
   * `AUDIT_LEDGER_NOTE`, present on EVERY payload including an empty one: an absent entry is not proof
   * that nothing happened. The page renders it; it is the reason the surface exists as a compliance
   * read, so a UI that drops it has removed the one thing that makes an empty table honest.
   */
  note: string;
}

/** The door's own two filters — exact matches on the stored top-level keys, empty means all. */
export interface ListAuditQuery {
  kind?: string;
  device_id?: string;
  cursor?: string;
  limit?: number;
}

/* ------------------------------------------------------------------ */
/* signing keys — `GET /ops/v1/keys` (U-08-b door, U-16-e1 page)        */
/* ------------------------------------------------------------------ */

/**
 * One signing key this deployment publishes, in metadata only.
 *
 * The five fields are the whole payload (`acs_service/models.py::SigningKeyMetadata`), and the pair
 * of names matters: `kid` and `alg` are as published, `created_at` is read off the kid this service
 * mints (`acs-<epoch>-<token>`), `not_after` is that instant plus the recorded 90-day discipline, and
 * `active` is the key `active_key()` would sign with. **No key material of either half** — the private
 * `d` never leaves `ACS_SECRET_DIR` (AGENTS invariant ۴) and the public `x` is what
 * `/.well-known/jwks.json` exists to publish, so this door duplicates nothing and this type must not
 * grow a field that says otherwise (`test/keysShapes.test.ts` reads the forbidden names out of the
 * server's own measurement).
 *
 * `created_at`/`not_after` are nullable because a published kid this service did not mint answers two
 * nulls rather than a date nobody recorded — the page renders that as unknown, never as a guess
 * (the same rule U-06-e set for a `null` cell).
 */
export interface SigningKeyMetadata {
  kid: string;
  alg: string;
  created_at: string | null;
  not_after: string | null;
  active: boolean;
}

/**
 * `GET /ops/v1/keys` → every published signing key.
 *
 * There is no `total`, no `next_cursor` and no `note` here: the door declares no filters and no
 * pagination, and it is a read of the published JWKS rather than an append-only ledger, so the
 * honesty clause belongs to the ledger door and not to this one. Inventing a `note` field would be
 * the console claiming a server promise nobody made.
 */
export interface SigningKeyListResponse {
  keys: SigningKeyMetadata[];
}

/* ------------------------------------------------------------------ */
/* operator profile — the six doors of `routers/profile.py` (U-06, page U-16-e2) */
/* ------------------------------------------------------------------ */

/**
 * One line of the validator's report — three strings and that is the whole contract
 * (`models.ProfileProblemReport`: a fourth key would be a second vocabulary the console has to
 * learn). There is no severity: §۲ wants a report fixable in one pass, and «this one is only a
 * warning» is exactly where that vocabulary dies.
 */
export interface ProfileProblemReport {
  path: string;
  rule: string;
  detail: string;
}

/**
 * One changed path between the stored profile and a candidate (`models.ProfileDiffEntry`).
 * `before` is `null` when nothing is stored there — «absent», «null» and «a value» are different
 * facts (§۲:۳۹'s three-state rule applied to the diff itself), so the console renders the null
 rather than folding it into a zero.
 */
export interface ProfileDiffEntry {
  path: string;
  before?: unknown;
  after?: unknown;
}

/**
 * `POST /ops/v1/operator-profile:validate` → the whole report, every complaint, in one answer
 * (`models.ProfileValidationReport`). `valid` and `release_blocked` are separate questions: a
 * release-blocked candidate is `valid: false, release_blocked: true`, and the pair is what buys the
 * activation door its status choice (409 vs 422). `base_version` is null when nothing has been
 * activated — `0` would name a version no history row carries.
 */
export interface ProfileValidationReport {
  valid: boolean;
  release_blocked: boolean;
  problems: ProfileProblemReport[];
  diff: ProfileDiffEntry[];
  base_version: number | null;
}

/**
 * The open candidate document — judged by `operator_profile.py` (strict, `extra="forbid"`), never
 * by a second schema here: a browser-side model would answer the first complaint instead of all of
 * them, and §۲ step ۲ forbids exactly that. Keys are kept verbatim, never dropped.
 */
export type ProfileDocument = Record<string, unknown>;

/**
 * `GET /ops/v1/operator-profile` → the active document verbatim, the only export format
 * (`models.ProfileReadResponse`). The `note` is required because the two nulls need a sentence:
 * «no version» and «no profile at all» read the same in a console that has to guess.
 */
export interface ProfileReadResponse {
  active_version: number | null;
  document: ProfileDocument | null;
  note: string;
}

/** Body of `PUT /ops/v1/operator-profile` (`models.ProfileActivationRequest`). */
export interface ProfileActivationRequest {
  /** Required and never defaulted: an absent field is a caller that did not say what it read. */
  expected_version: number;
  document: ProfileDocument;
}

/**
 * Body of `POST /ops/v1/operator-profile:rollback` (`models.ProfileRollbackRequest`). `confirm`
 * must be literally `true` — absent and false are 422s, not a default; a string "true" is a no
 * because the model is strict.
 */
export interface ProfileRollbackRequest {
  to_version: number;
  expected_version: number;
  confirm: true;
}

/**
 * `PUT` and `:rollback` share this answer (`models.ProfileActivationAccepted`). `previous_version`
 * is null for the first activation ever, `rollback_of` is null on a normal activation and the
 * stored version whose content came forward on a rollback — which is NOT the version this response
 * activated, because a rollback lands as a NEW forward version (§۲:۵۷).
 */
export interface ProfileActivationAccepted {
  active_version: number;
  previous_version: number | null;
  rollback_of: number | null;
  note: string;
}

/**
 * One history row (`models.ProfileVersionRow`). The actor is the NESTED `{kind, subject}` pair the
 * profile door stores and `/versions` re-publishes — U-16-b ruling D2 keeps this typed surface as
 * it is, and the page renders it through `lib/auditActor.ts` shape (ج). No row carries a document:
 * `GET /ops/v1/operator-profile` is the only export.
 */
export interface ProfileVersionRow {
  version: number;
  activated_at: string;
  actor: Record<string, string>;
  changed_keys: string[];
}

/**
 * `GET /ops/v1/operator-profile/versions` → newest first, bounded page (`ProfileVersionListResponse`).
 * `count` counts THIS page; `has_more` says older rows exist beyond it, so a full page never reads
 * as the whole history.
 */
export interface ProfileVersionListResponse {
  count: number;
  has_more: boolean;
  versions: ProfileVersionRow[];
  note: string;
}

/**
 * `GET /ops/v1/operator-profile/readiness` → the report that reports and decides nothing
 * (`ProfileReadinessResponse`). `release_blocked` is required, never defaulted; every fleet row
 * carries `write_authorized: false` BY DESIGN (§۲:۴۱ — the witness gate is the only authority that
 * grants a write, and this door does not even read it). `capacity` is integer arithmetic over what
 * the operator declared, labelled `measured: false`, with null inputs echoed in `unknown_inputs`.
 */
export interface ProfileReadinessResponse {
  profile_version: number | null;
  environment: string | null;
  release_blocked: boolean;
  unknown_inputs: string[];
  unresolved_dependencies: string[];
  fleet: Record<string, unknown>[];
  capacity: Record<string, unknown>;
  note: string;
}

/* ------------------------------------------------------------------ */
/* fleet jobs — `GET /ops/v1/jobs` (`routers/system.py:177`, U-16-f)    */
/* ------------------------------------------------------------------ */

/**
 * One row of `GET /ops/v1/jobs` — `acs_service/models.py::JobListItem`, field for field.
 *
 * The four timestamps and the two payload fields are what the server recorded; nothing here is
 * derived in the browser. `status` is the **lifecycle** (`queued|running|completed|failed`, plus
 * `timeout`/`cancelled` from `job_machine.TERMINAL`) and is deliberately typed as a plain string,
 * not as `JobStatus`: `job_machine` owns that alphabet and a console enum would silently drop a
 * word the server adds. The **technical claim about the device** is `verdict`, which lives on the
 * full job record (`GET /integration/v1/jobs/{job_id}`) and never on this row — a list row cannot
 * say a write landed (invariant ۳ / ruling D6).
 *
 * `result`/`error` are echoed as they arrived (`Any` on the wire) so a page renders the server's own
 * sentence instead of re-summarising it.
 */
export interface JobListRow {
  job_id: string;
  device_id: string;
  action: string;
  status: string;
  requested_at: string | null;
  started_at: string | null;
  completed_at: string | null;
  result: unknown;
  error: unknown;
}

/**
 * `JobsListResponse` — `total` is the count matching THIS filter over the whole set, not the page
 * (`store.list_jobs_filters` returns `(rows, total)`), so the UI must phrase it as «matching the
 * filter», never as «the fleet has N jobs».
 */
export interface JobsListResponse {
  items: JobListRow[];
  total: number;
  next_cursor: string | null;
}

/**
 * The door's own three filters plus the paging pair.
 *
 * `type` is an **ACTION PREFIX**, not an enum: `routers/system.py` documents «``diagnostics:`` → the
 * probe family, ``activation`` → ZTP runs, ``apply_policy`` → one verb» and hands it to
 * `store.list_jobs_filters(action_prefix=…)`. A filter that matches nothing is an empty page, never a
 * 404 — including an unknown `device`, which is a query and not a lookup.
 */
export interface ListJobsQuery {
  device?: string;
  status?: string;
  type?: string;
  cursor?: string;
  limit?: number;
}

/* ------------------------------------------------------------------ */
/* per-device operations / bulk — `routers/operations.py` (U-16-f)      */
/* ------------------------------------------------------------------ */

/**
 * One catalog slot as the gate sees it for this device (`OperationSlotItem`).
 *
 * Locked slots are LISTED, never dropped (rule B-6 in the module docstring: dropping a row would be
 * concealment), so `available: false` arrives with a `locked_reason` and a `detail` naming the next
 * step. `writable` is `null` wherever there is no write door, so a read-only sensor can never look
 * like an action. `rpc` is what an action row *is*, and the console needs it to build the typed
 * confirmation `:run` demands.
 */
export interface OperationSlotItem {
  op: string;
  /** `"parameter"` | `"action"` — an action row has no value to read (§۲.۴'s B-4). */
  kind: string;
  value_type: string;
  secret: boolean;
  root: string;
  available: boolean;
  writable: boolean | null;
  provenance: string | null;
  locked_reason: string | null;
  path: string | null;
  rpc: string | null;
  captured_at: string | null;
  detail: string;
}

/**
 * Which document answered for this device — or the honest statement that none did.
 * At rung ۶ the shape does not change: `manifest_id: null`, `matched_on: "none"`, every slot locked
 * `manifest-unresolved` (rule Rung-۶-is-a-state, module docstring).
 */
export interface OperationsManifestBlock {
  manifest_id: string | null;
  manifest_version: number;
  matched_on: string;
  ladder_rung: number;
  data_model_root: string;
  provenance_min: string;
}

export interface OperationsResponse {
  items: OperationSlotItem[];
  manifest: OperationsManifestBlock;
  next_cursor: string | null;
}

/** `GET …/operations` filters: `root` is `tr098|tr181` and an unknown one answers 422, not an empty list. */
export interface ListOperationsQuery {
  root?: string;
  available?: boolean;
  cursor?: string;
  limit?: number;
}

/** One resolved chain member of a value read (`ValuePathEntry`). */
export interface OperationValuePathEntry {
  root: string;
  path: string;
  value: unknown;
  captured_at: string | null;
  fault_code: string | null;
}

/**
 * `GET …/operations/{op}/value` → one slot's value with the facts that make the read auditable.
 *
 * `source` is the point of the payload: `snapshot` (the façade's cache), `acs-direct`, `olt-snmp`
 * (§۹ — an optical slot has no CWMP binding by design) or `none` (honest emptiness). No value here
 * claims a CWMP round trip happened, so the console must not either. A secret arrives masked with
 * only its `value_length` (B-5) — never its content.
 */
export interface OperationValueResponse {
  op: string;
  kind: string;
  value: unknown;
  value_type: string;
  secret: boolean;
  stale: boolean;
  value_length: number | null;
  source: string;
  captured_at: string | null;
  provenance: string | null;
  manifest_id: string | null;
  manifest_version: number;
  available: boolean;
  locked_reason: string | null;
  paths: OperationValuePathEntry[];
  detail: string;
}

/** `PUT …/operations/{op}` body: `{ value, confirm?, readback, batch_hint? }`. No `force` field exists. */
export interface OperationWriteRequest {
  value?: unknown;
  confirm?: string | null;
  readback?: boolean;
  batch_hint?: number | null;
}

/**
 * `202` from `PUT …/operations/{op}` — an acceptance, nothing more.
 * `status` is the lifecycle the door recorded (`queued`); the model carries no `verdict` field and
 * this type must not grow one, because the claim about the device is the read-back's, not the
 * acceptance's (§۴.۳ / D6).
 */
export interface OperationWriteAccepted {
  job_id: string;
  status: string;
}

/** `POST …/operations/{op}:run` body: `{ confirm?, reason? }` — no value, no path, no parameters. */
export interface OperationRunRequest {
  confirm?: string | null;
  reason?: string | null;
}

/** `202` from the RPC door: the method that was handed over, and nothing about its effect. */
export interface OperationRunAccepted {
  job_id: string;
  status: string;
  rpc: string;
  op: string;
}

/** `PUT …/parameters/raw` body (§۴.۵): N full paths → values, plus the typed confirmation. */
export interface RawWriteRequest {
  paths: Record<string, unknown>;
  confirm?: string | null;
  witness_id?: string | null;
  reason?: string | null;
}

/* ------------------------------------------------------------------ U-16-g1: identity + ops settings --- */

/**
 * `routers/identity.py::USER_STATUSES` — the two statuses a user row may carry.
 *
 * This is a COPY of the server's tuple, and `test/settingsFixture.test.ts` is what keeps it one: it
 * parses the tuple out of `acs_service/routers/identity.py` and refuses a drift, for the same reason
 * `fixtures/session.ts` copies §۴'s table rather than inventing a picker (`navigationCapabilities`).
 * A page that hard-coded a third status would offer a value the door answers 422 for.
 */
export const USER_STATUSES = ['active', 'disabled'] as const;
export type UserStatus = (typeof USER_STATUSES)[number];

/** `models.py::ConsoleUser` — one console user with every secret field dropped. */
export interface ConsoleUser {
  user_id: string;
  username: string;
  role: string | null;
  status: string;
  created_at: string | null;
  updated_at: string | null;
}

/** `200` from `GET /ops/v1/users` — the complete table, deliberately unpaged (the door says why). */
export interface UserListResponse {
  count: number;
  users: ConsoleUser[];
}

/** `POST /ops/v1/users` body (`models.py::UserCreateRequest`): `role` defaults to `user` server-side. */
export interface UserCreateRequest {
  username: string;
  password: string;
  role?: string;
}

/**
 * `PATCH /ops/v1/users/{user_id}` body (`models.py::UserPatchRequest`).
 *
 * Absent means «leave it alone», never «set the default» — that is why the page sends only the field
 * the operator touched, and why a password change is its own request rather than part of a role write.
 */
export interface UserPatchRequest {
  role?: string;
  status?: string;
  password?: string;
}

/** `200` from the three user-writing doors: the row as it now stands plus the door's own sentence. */
export interface UserMutationAccepted {
  user: ConsoleUser;
  detail: string;
}

/**
 * `200` from `GET`/`PUT /ops/v1/settings` (`models.py::OpsSettingsRead`).
 *
 * `values` is every whitelisted key with the stored override else the default, `defaults` the same key
 * set as the server's table, `updated_at` is null while nothing has been written. `note` is
 * `SETTINGS_INTENT_NOTE` — the honesty clause of this surface, and part of the contract: a page that
 * dropped it would render "recorded intent" as "applied config".
 */
export interface OpsSettingsRead {
  values: Record<string, number>;
  defaults: Record<string, number>;
  updated_at: string | null;
  note: string;
}

/* ------------------------------------------------------------------ */
/* permission matrix — `GET`/`PUT /ops/v1/permissions` (U-16-g2)        */
/* ------------------------------------------------------------------ */

/**
 * `200` from `GET /ops/v1/permissions` (`models.py::PermissionMatrix`) — the whole grid the server
 * enforces, for the owner who edits it.
 *
 * `capabilities` is capability → role → level and it is *complete*: every key in the registry appears
 * with every published role, and a cell the store says nothing about reads `none` (§۳-۴). The editor
 * renders this answer and nothing else — `roles` and `levels` travel with the grid precisely so the
 * console never holds a second copy of §۱/§۲ to drift from. Here the `levels` alphabet is the door's
 * own (`none` included), NOT `CAPABILITY_WIRE_LEVELS`: the effective door omits `none` cells by
 * contract, the matrix door names them, and the two spellings are one vocabulary seen from two doors.
 */
export interface PermissionMatrixRead {
  capabilities: Record<string, Record<string, string>>;
  roles: string[];
  levels: string[];
}

/**
 * One cell of a matrix write (`models.py::MatrixCell`): three plain strings, and none is an enum on
 * the wire — the door answers `reference-invalid` rather than a schema error when one names something
 * outside the vocabulary, because "the body was malformed" and "the body named what this deployment
 * does not have" are different answers to an operator.
 */
export interface MatrixCellWrite {
  capability: string;
  role: string;
  level: string;
}

/** Body of `PUT /ops/v1/permissions` (`models.py::MatrixWriteRequest`): one or more cells, judged together. */
export interface MatrixWriteBody {
  cells: MatrixCellWrite[];
}

/** One cell as the write left it — the same shape the `permission.change` audit row records (§۶). */
export interface MatrixCellChange {
  capability: string;
  role: string;
  before: string;
  after: string;
}

/**
 * `200` from `PUT /ops/v1/permissions` (`models.py::MatrixMutationAccepted`).
 *
 * `changed` carries every requested cell INCLUDING one that already held the requested level (then
 * `before` equals `after`, which is how a replay stays equal); `detail` says how many actually moved,
 * so "nothing changed" is never dressed up as a change; `capabilities` answers the editor's next read
 * without a second call.
 */
export interface MatrixMutationAccepted {
  changed: MatrixCellChange[];
  capabilities: Record<string, Record<string, string>>;
  detail: string;
}

/* ------------------------------------------------------------------ */
/* System measurement — `GET /ops/v1/system/components` ·            */
/* `GET /ops/v1/system/queues` (O4)                                  */
/* ------------------------------------------------------------------ */

/**
 * The queue depths, in the server's CLOSED vocabularies.
 *
 * The store counts the outbox by its four states and the jobs by the seven
 * lifecycle words, and it answers the WHOLE vocabulary today — a word
 * nobody holds is a MEASURED zero, because the store counted it and found
 * none. The door preserves whatever the store reported: a word the store did
 * NOT report is simply not in the map, and this type keeps that distinction:
 * the maps are `Record<string, number>`, never a fixed set of numbers, so
 * nothing here can invent a zero the server did not send.
 */
export interface SystemQueueDepths {
  outbox: Record<string, number>;
  jobs: Record<string, number>;
}

/**
 * The evidence a component carries: the backend name and the counts for the
 * store, the mode and the probe answer for the NBI and the adapter, the
 * error when a component is down.
 *
 * `depths` is the store component's queue depths — `null` when the server
 * could not measure them and ABSENT when the server never tried. Neither is
 * a zero: a console that shows «0» for a measurement the façade never made
 * is lying, and that is the one thing this surface may not do.
 */
export interface SystemComponentDetail {
  backend?: string;
  counts?: Record<string, number>;
  depths?: SystemQueueDepths | null;
  [key: string]: unknown;
}

/** One component of the components door: `up` or `down`, and the evidence. */
export interface SystemComponent {
  status: 'up' | 'down';
  detail: SystemComponentDetail;
}

/**
 * `GET /ops/v1/system/components` — the EVIDENCE view of the four components
 * (api / store / nbi / adapter).
 *
 * The same four components and the same `status` per component as the health
 * rollup (`GET /ops/v1/system/health`), with the store's detail also
 * carrying its queue depths. A down component is a STATE: the door answers
 * 200, and `status` is `degraded`.
 */
export interface SystemComponentsResponse {
  service: 'acs-api';
  status: 'ok' | 'degraded';
  components: Record<string, SystemComponent>;
  contract_version: 'v1';
  version: string;
  time: string;
}

/**
 * `GET /ops/v1/system/queues` — the depths and the live session count.
 *
 * `note` carries the honesty clause: which metrics the façade deliberately
 * does NOT publish (session rate, job latency, adapter errors, inform rate)
 * and why. A metric that cannot be counted honestly is ABSENT, never zero.
 */
export interface SystemQueuesResponse {
  outbox: Record<string, number>;
  jobs: Record<string, number>;
  sessions_active: number;
  contract_version: 'v1';
  time: string;
  note: string;
}

/**
 * The three sentences one worker's heartbeat row can be (DA-09). They are not
 * interchangeable and the console may not collapse them:
 *  - `ok` — beaten inside its bound and the last pass succeeded;
 *  - `failing` — beaten inside its bound, last pass ended in the exception path. The
 *    worker is ALIVE and the next tick is the retry;
 *  - `stale` — no beat since before `3 × interval + 60s`, whatever the last pass said.
 *    `stale` outranks `failing`, and the server has already applied that precedence.
 */
export type WorkerBeatState = 'ok' | 'failing' | 'stale';

/**
 * `GET /ops/v1/system/workers` — one worker's row, as the door judged it.
 *
 * `age_seconds` is `null` when the stored instant could not be parsed: «I cannot prove
 * this worker is alive» is a different answer from an age of zero, and a console that
 * renders the null as `0 ثانیه پیش` invents the one fact the door refused to state.
 * `interval_seconds` and `stale_after_seconds` are nullable for the same reason — the
 * row carries what the worker stamped, and nothing downstream may fill the gap.
 */
export interface SystemWorkerBeat {
  worker: string;
  status: WorkerBeatState;
  owner: string;
  at: string;
  age_seconds: number | null;
  interval_seconds: number | null;
  stale_after_seconds: number | null;
  beats: number;
  failures: number;
  detail: Record<string, unknown>;
}

/**
 * `GET /ops/v1/system/workers` — which loops have beaten, how long ago, and how their
 * last pass went (DA-09, given a screen by TLM-4b).
 *
 * `workers` carries a row ONLY for a worker that has beaten at least once. A worker this
 * façade has never heard from is ABSENT from the object — not `down`, not `ok`: the API
 * cannot know whether the deployment runs it. `status` is the rollup over the rows that
 * exist, and `error` is the door's own «I could not look» (with NO rows, because an empty
 * object is also the honest answer for a deployment that runs no workers at all).
 */
export interface SystemWorkersResponse {
  service: 'acs-api';
  status: 'ok' | 'degraded';
  workers: Record<string, SystemWorkerBeat>;
  note: string;
  error: string;
  contract_version: 'v1';
  version: string;
  time: string;
}

/* ---- the subscriber surface (S1, G-SUB-01/03) ----
   The subscriber entity and the service profile, as `openapi/openapi.yaml` publishes them
   (`Subscriber` · `SubscriberCreateRequest` · `SubscriberListResponse` · `ServiceProfile` ·
   `ServiceProfileDba` · `ServiceProfileWriteRequest` · `ServiceProfileListResponse`).

   THE MASK IS THE SERVER'S, NOT THE CONSOLE'S. The door decides which fields a
   caller may see by the caller's LEVEL on `subscriber.read`: at `edit` the
   identifying fields are whole, at `read` they are masked (the length kept; a value
   of two characters or fewer is masked ENTIRELY, and a longer value has every
   character but the last two replaced by `*`). The console therefore has no
   `?full=` flag and no body flag to invent — it renders the answer it is given,
   and a masked value is not a missing value. */

/** The subscriber's status vocabulary — `SUBSCRIBER_STATUSES` of `acs_service/models.py`. */
export const SUBSCRIBER_STATUSES = ['active', 'suspended'] as const;

/** The three fields that are personal data — `SUBSCRIBER_IDENTIFYING_FIELDS`. */
export const SUBSCRIBER_IDENTIFYING_FIELDS = ['mobile', 'national_id', 'contract_number'] as const;

/** One subscriber row, as THIS caller may see it. */
export interface Subscriber {
  subscriber_id: string;
  mobile: string;
  national_id: string | null;
  contract_number: string | null;
  status: string;
  acs_device_id: string | null;
  service_profile_id: string | null;
  /**
   * The BSS's OWN reference to this subscriber — the same `^sub:` string the frozen v1
   * `subscriber_ref` names, and the name every binding the BSS has already written
   * carries. It is the row's SECOND identity (the first is `subscriber_id`, which this
   * service minted), and it is what joins the row to those bindings.
   *
   * It is NULLABLE — a subscriber the ACS minted itself has no BSS reference at all —
   * and the console must not pretend otherwise in either direction: the server sends
   * `null` for a row without one, and the console reads `null` as «no BSS reference»
   * rather than as a missing field.
   */
  bss_subscriber_ref: string | null;
  created_at: string;
  updated_at: string;
}

/** `GET /ops/v1/subscribers` — the page and the number BEHIND it. */
export interface SubscriberListResponse {
  items: Subscriber[];
  total: number;
  limit: number;
  offset: number;
}

/** The filters of the subscriber list door: one status word, and the page bounds. */
export interface ListSubscribersQuery {
  status?: 'active' | 'suspended';
  offset?: number;
  limit?: number;
}

/** The filters of the service-profile list door: the page bounds only. */
export interface ListServiceProfilesQuery {
  offset?: number;
  limit?: number;
}

/**
 * The body of `POST /ops/v1/subscribers`. THE IDENTITY IS NOT IN IT: the door
 * mints the row's own key, so a body that could name its own key would let a
 * caller choose which row it creates.
 */
export interface SubscriberCreateRequest {
  mobile: string;
  national_id?: string | null;
  contract_number?: string | null;
  status?: string;
  acs_device_id?: string | null;
  service_profile_id?: string | null;
  /**
   * The BSS's OWN reference to the subscriber this body creates — the same opaque `^sub:`
   * string the frozen v1 `subscriber_ref` names, which the BSS mints itself and this ACS
   * never does. It is OPTIONAL (a subscriber the ACS minted has no BSS reference) and
   * UNIQUE where it is present: a taken one is refused with the existing
   * `409 binding-conflict`.
   */
  bss_subscriber_ref?: string | null;
}

/** The DBA shape a service profile carries: the container and its two ceilings. */
export interface ServiceProfileDba {
  tcont: string;
  assured_mbps: number | null;
  maximum_mbps: number | null;
}

/**
 * One service-profile row. NO personal data lives here — a profile is a
 * commercial shape (a speed, a VLAN, a DBA), not a person — so every reader of
 * `service.profile` sees the same fields and this schema carries no mask.
 */
export interface ServiceProfile {
  profile_id: string;
  name: string;
  down_mbps: number;
  up_mbps: number;
  vlan_id: number;
  dba: ServiceProfileDba;
  created_at: string;
  updated_at: string;
}

/** `GET /ops/v1/service-profiles` — the page and the number BEHIND it. */
export interface ServiceProfileListResponse {
  items: ServiceProfile[];
  total: number;
  limit: number;
  offset: number;
}

/**
 * The body of `POST` and `PUT` on the service-profile doors — ONE shape for
 * both, because `PUT /ops/v1/service-profiles/{profile_id}` is a FULL
 * REPLACEMENT: the body IS the profile, not a patch of it.
 */
export interface ServiceProfileWriteRequest {
  name: string;
  down_mbps: number;
  up_mbps: number;
  vlan_id: number;
  dba: ServiceProfileDba;
}

/* ---- the subscriber search + the 360-degree view (S2/S3, G-SUB-08 / G-PRC-14) ----
   The two doors of `POST /integration/v1/devices:search` and
   `GET /ops/v1/subscribers/{subscriber_id}/overview`, as the console's copy of their
   published shapes (`SubscriberSearchRequest` · `SubscriberSearchHit` ·
   `SubscriberSearchResponse` · `SubscriberDeviceView` · `SubscriberBindingView` ·
   `SubscriberOverview`).

   **THE CRITERIA TRAVEL IN THE BODY, NOT IN THE QUERY STRING.** A search by a phone
   number in a URL is a phone number in the access log, in the proxy's log and in the
   browser's history — so the console's search method takes the criteria as a body and
   the HTTP provider sends it as one.

   **THE MASK IS STILL THE SERVER'S.** The search door declares `fleet.devices.read`,
   and the LEVEL on that key decides the identifying fields: at `read` they come back
   masked, at `edit` they come back whole. The console sends no flag that could change
   it, and the parser never masks again.

   **`bss_subscriber_ref` IS NEVER MASKED**, because it is an IDENTIFIER and not
   personal data — the party that minted it is the party that must read it, and a
   masked identity is an identity nobody can join on.

   **A SECTION WHOSE ROW DOES NOT EXIST IS ABSENT (`null`) — NEVER INVENTED.** The
   360-degree view publishes six sources and no seventh, and each of them says which
   one it is. A subscriber with no device has no device section. */

/**
 * The five criteria the search accepts — `SUBSCRIBER_SEARCH_FIELDS` of
 * `acs_service/models.py`, in the door's own order.
 *
 * Named once in this console so the fixture, the audit row and the guard all read the
 * SAME list: a criterion the server knows and the console does not is a search nobody
 * can perform, and a criterion the console knows and the server does not is a search
 * that would be refused.
 */
export const SUBSCRIBER_SEARCH_CRITERIA = [
  'mobile',
  'national_id',
  'contract_number',
  'subscriber_ref',
  'serial',
] as const;

/** The page bound the door publishes — `1..200`, default `50` (the outbox's cap). */
export const SUBSCRIBER_SEARCH_MIN_LIMIT = 1;
export const SUBSCRIBER_SEARCH_MAX_LIMIT = 200;
export const SUBSCRIBER_SEARCH_DEFAULT_LIMIT = 50;

/**
 * The body of `POST /integration/v1/devices:search` — the criteria of ONE search.
 *
 * AT LEAST ONE CRITERION MUST BE GIVEN: a body that names none is 422
 * `reference-invalid` and never becomes an unfiltered read, because «every subscriber»
 * is a question this door does not answer.
 *
 * The criteria are a CONJUNCTION — a row must satisfy every criterion given. The
 * three identifying fields are fields of the SUBSCRIBER row; `subscriber_ref` is the
 * subscriber's NAME and matches EITHER of its two identities (the one this service
 * minted and the BSS's own); `serial` is a field of the DEVICE row, and the answer is
 * the subscriber who HOLDS that device.
 *
 * **Every criterion is OPTIONAL and NULLABLE** — the server's model takes `null` for
 * each of them and the published schema says `nullable: true` for each of them, so a
 * `null` is «no constraint on this field» and never «the empty string».
 */
export interface SubscriberSearchRequest {
  mobile?: string | null;
  national_id?: string | null;
  contract_number?: string | null;
  subscriber_ref?: string | null;
  serial?: string | null;
  /** The page size: `1..200`, default `50`. */
  limit?: number;
}

/**
 * One subscriber row the search matched, as THIS caller may see it.
 *
 * The identifying fields (`mobile` · `national_id` · `contract_number`) are the ones
 * the mask applies to; the identity, the status, the two links and the store's own
 * stamps are not personal data and are never masked.
 */
export interface SubscriberSearchHit {
  subscriber_id: string;
  mobile: string;
  national_id: string | null;
  contract_number: string | null;
  status: string;
  acs_device_id: string | null;
  service_profile_id: string | null;
  /** The BSS's OWN reference — an IDENTIFIER, never masked. */
  bss_subscriber_ref: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * `POST /integration/v1/devices:search` — the page, and the number BEHIND it.
 *
 * `total` is the number of rows behind the page, not the size of the page.
 */
export interface SubscriberSearchResponse {
  items: SubscriberSearchHit[];
  total: number;
  limit: number;
}

/**
 * The DEVICE row of the 360-degree view.
 *
 * EVERY FIELD NAMES ITS SOURCE — the device row. Nothing here is derived and nothing
 * here is computed, which is why this is NOT the published `DeviceSummary`: that shape
 * carries a `stale` flag NO DEVICE ROW CARRIES, and a view that published a derived
 * field would be publishing a field nobody read.
 */
export interface SubscriberDeviceView {
  acs_device_id: string;
  serial: string | null;
  oui: string | null;
  product_class: string | null;
  manufacturer: string | null;
  software_version: string | null;
  online: boolean;
  last_inform_at: string | null;
}

/**
 * The BINDING row of the 360-degree view — the MIRROR of the claim.
 *
 * The subscriber row is the AUTHORITY on who holds a device and the binding row is its
 * mirror; the view publishes both, and each of them says which one it is. The binding's
 * `subscriber_ref` may be EITHER of the subscriber row's two identities, and it is
 * never masked — it is an identifier, not personal data.
 */
export interface SubscriberBindingView {
  acs_device_id: string;
  nas_ref: string | null;
  subscriber_ref: string | null;
  binding_version: number;
  bound_at: string | null;
}

/**
 * The 360-degree view of one subscriber — and each section names ITS SOURCE.
 *
 * `subscriber` is the SUBSCRIBER row (masked by the caller's level on
 * `subscriber.read`); `device` is the DEVICE row; `binding` is the BINDING row;
 * `service_profile` is the SERVICE-PROFILE row; `alarms` is a BOUNDED PAGE of the
 * device's alarm rows; `jobs` is a BOUNDED PAGE of the device's job rows.
 *
 * The two lists carry the shapes the doors publish for them: the alarm row is the
 * alarm feed's own row, and the job row is the fleet job list's own row — the console
 * calls that one `JobListRow`, which is the published `JobListItem` field for field.
 */
export interface SubscriberOverview {
  subscriber: Subscriber;
  device: SubscriberDeviceView | null;
  binding: SubscriberBindingView | null;
  service_profile: ServiceProfile | null;
  alarms: Alarm[];
  jobs: JobListRow[];
}

/**
 * The provisioning presets (GenieACS "presets"), as the read door publishes them —
 * a passthrough of the NBI's whole list (`GET /ops/v1/templates`, routers/system.py).
 * Each item is the preset document verbatim: the console never reshapes what the NBI
 * accepted. `note` is present only in canned mode and names the test double instead of
 * pretending to report a live GenieACS.
 */
export interface TemplateListResponse {
  items: Record<string, unknown>[];
  total: number;
  note: string | null;
}

/**
 * The answer of `PUT /ops/v1/templates/{name}` — the document echoed verbatim. There is
 * deliberately NO created/replaced field: a live 2xx only says the NBI took the document
 * (G-PRV-05), and whether a same-named preset existed before is a fact this layer did not
 * measure.
 */
export interface PresetWriteResponse {
  name: string;
  document: Record<string, unknown>;
  note: string | null;
}

/**
 * One reconciliation report (`GET /ops/v1/reconciliation`), as the C6 worker will write
 * it — `report` is the audit entry's own fields verbatim; the façade invents no summary
 * keys for a worker that does not exist yet.
 */
export interface ReconciliationRun {
  run_id: string;
  at: string;
  report: Record<string, unknown>;
}

/** The list answer. `note` is carried in EVERY payload: an empty list means no run has
 * been recorded yet, not that the fleet reconciles. */
export interface ReconciliationListResponse {
  runs: ReconciliationRun[];
  total: number;
  next_cursor: string | null;
  note: string;
}
